<template>
  <div class="region-tree" :class="{ 'region-tree--root': is_root, 'region-tree--nested': !is_root }">
    <v-sheet v-if="is_root" class="region-tree-root-sheet rounded-lg overflow-hidden" elevation="3">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        item-key="id"
        class="region-tree-table region-tree-table--root"
        show-expand
        :expanded.sync="expanded"
        mobile-breakpoint="1"
        dense
        hide-default-footer
        disable-pagination
      >
        <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
          <v-btn
            v-if="item.has_children"
            icon
            small
            class="region-tree-expand-btn"
            aria-label="Expandir ou recolher filhos"
            @click.stop="toggleRootExpand(item, isExpanded, expand)"
          >
            <v-progress-circular
              v-if="loading_children_by_id[item.id]"
              indeterminate
              size="14"
              width="2"
              color="primary"
            />
            <v-icon v-else small color="primary">{{ isExpanded ? 'mdi-menu-down' : 'mdi-menu-right' }}</v-icon>
          </v-btn>
          <span v-else class="region-tree-expand-placeholder" />
        </template>

        <template v-slot:item.name="{ item }">
          <span class="region-tree-name">{{ item.name }}</span>
        </template>

        <template v-slot:item.kind="{ item }">
          <v-chip small label outlined :color="kindChipColor(item.kind)" class="region-tree-kind-chip">
            {{ kindLabel(item.kind) }}
          </v-chip>
        </template>

        <template v-slot:item.code="{ item }">
          <span class="text-caption text--secondary">{{ item.code || '—' }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center align-center flex-wrap region-tree-actions">
            <v-btn v-if="userCanEdit && canAddChild(item)" small icon class="success mr-1" title="Adicionar região filha" @click.stop="$emit('add-child', item)">
              <v-icon class="white--text" small>mdi-plus</v-icon>
            </v-btn>
            <v-btn v-if="userCanEdit" small icon class="primary mr-1" @click.stop="$emit('edit', item)">
              <v-icon class="white--text" small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn v-if="userCanEdit" small icon class="error" @click.stop="$emit('delete', item)">
              <v-icon class="white--text" small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>

        <template v-slot:expanded-item="{ headers: hdr, item }">
          <td :colspan="hdr.length" class="pa-0 region-tree-expanded-td">
            <div class="region-tree-nested-panel">
              <region-nested-table
                :parent-id="item.id"
                :depth="depth + 1"
                :search="search"
                :global-search="globalSearch"
                :search-expand-plan="search_expand_plan_for_children"
                :user-can-edit="userCanEdit"
                :refresh-counter="refreshCounter"
                @edit="$emit('edit', $event)"
                @delete="$emit('delete', $event)"
                @add-child="$emit('add-child', $event)"
                @loading-change="setChildLoading"
              />
            </div>
          </td>
        </template>

        <template v-slot:no-data>
          <div class="py-6 text-center text--secondary text-body-2">Nenhuma região neste nível.</div>
        </template>
      </v-data-table>
    </v-sheet>

    <div v-else class="region-branch">
      <div class="region-branch__rail" aria-hidden="true" />
      <div class="region-branch__inner">
        <v-overlay :value="loading" absolute opacity="0.06" color="primary" />
        <v-simple-table dense class="region-branch-table transparent elevation-0">
          <thead>
            <tr>
              <th class="region-branch-th region-branch-th--narrow" />
              <th class="region-branch-th text-left">Nome</th>
              <th class="region-branch-th text-center">Tipo</th>
              <th class="region-branch-th text-center">Código</th>
              <th class="region-branch-th text-center region-branch-th--actions">Ações</th>
            </tr>
          </thead>
          <tbody v-for="item in items" :key="'tbody-' + item.id">
              <tr class="region-branch-row">
                <td class="region-branch-td region-branch-td--narrow">
                  <v-btn
                    v-if="item.has_children"
                    icon
                    x-small
                    class="region-tree-expand-btn"
                    :aria-expanded="expanded_nested_ids.includes(item.id)"
                    @click.stop="toggleNestedExpand(item)"
                  >
                    <v-progress-circular
                      v-if="loading_children_by_id[item.id]"
                      indeterminate
                      size="12"
                      width="2"
                      color="primary"
                    />
                    <v-icon v-else x-small color="primary">
                      {{ expanded_nested_ids.includes(item.id) ? 'mdi-menu-down' : 'mdi-menu-right' }}
                    </v-icon>
                  </v-btn>
                  <span v-else class="region-tree-expand-placeholder region-tree-expand-placeholder--sm" />
                </td>
                <td class="region-branch-td">
                  <span class="region-tree-name text-body-2">{{ item.name }}</span>
                </td>
                <td class="region-branch-td text-center">
                  <v-chip small label outlined :color="kindChipColor(item.kind)" class="region-tree-kind-chip">
                    {{ kindLabel(item.kind) }}
                  </v-chip>
                </td>
                <td class="region-branch-td text-center">
                  <span class="text-caption text--secondary">{{ item.code || '—' }}</span>
                </td>
                <td class="region-branch-td text-center">
                  <div class="d-flex justify-center align-center flex-wrap">
                    <v-btn v-if="userCanEdit && canAddChild(item)" x-small icon class="success mr-1" title="Adicionar filha" @click.stop="$emit('add-child', item)">
                      <v-icon class="white--text" x-small>mdi-plus</v-icon>
                    </v-btn>
                    <v-btn v-if="userCanEdit" x-small icon class="primary mr-1" @click.stop="$emit('edit', item)">
                      <v-icon class="white--text" x-small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn v-if="userCanEdit" x-small icon class="error" @click.stop="$emit('delete', item)">
                      <v-icon class="white--text" x-small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
              <tr v-if="expanded_nested_ids.includes(item.id) && item.has_children" class="region-branch-subrow">
                <td colspan="5" class="pa-0 region-tree-expanded-td region-tree-expanded-td--nested">
                  <div class="region-tree-nested-panel region-tree-nested-panel--deep">
                    <region-nested-table
                      :parent-id="item.id"
                      :depth="depth + 1"
                      :search="search"
                      :global-search="globalSearch"
                      :search-expand-plan="search_expand_plan_for_children"
                      :user-can-edit="userCanEdit"
                      :refresh-counter="refreshCounter"
                      @edit="$emit('edit', $event)"
                      @delete="$emit('delete', $event)"
                      @add-child="$emit('add-child', $event)"
                      @loading-change="setChildLoading"
                    />
                  </div>
                </td>
              </tr>
          </tbody>
        </v-simple-table>

        <div v-if="!loading && items.length === 0" class="py-5 text-center text--secondary text-body-2">
          Nenhuma região neste nível.
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import Api from '@/api/index'
import {
  flattenRegionsTree,
  buildAncestorExpandPlan,
  unwrapRegionsIndexPayload,
  dedupeRegionRowsPreferDeepestPath,
} from './flattenRegionsTree.js'

let KIND_LABELS = {
  country: 'País',
  state: 'Estado',
  rgint: 'Região Geográfica Intermediária',
  rgi: 'Região Geográfica Imediata',
  city: 'Município',
  regional: 'Zona',
  neighborhood: 'Bairro',
}

let DEFAULT_EXPAND_KINDS = {
  0: ['country'],
  1: ['state'],
}

export default {
  name: 'RegionNestedTable',
  components: {
    RegionNestedTable: () => import('./RegionNestedTable.vue'),
  },
  props: {
    parentId: {
      type: Number,
      default: null,
    },
    depth: {
      type: Number,
      default: 0,
    },
    search: {
      type: String,
      default: '',
    },
    globalSearch: {
      type: Boolean,
      default: false,
    },
    userCanEdit: {
      type: Boolean,
      default: false,
    },
    refreshCounter: {
      type: Number,
      default: 0,
    },
    /** Plano de expansão na busca global (ancestrais até o match); repassado aos filhos. */
    searchExpandPlan: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      expanded: [],
      expanded_nested_ids: [],
      loading_children_by_id: {},
      /** Só na raiz: plano montado após index + matches */
      active_search_expand_plan: null,
      headers: [
        { text: 'Nome', value: 'name', sortable: false },
        { text: 'Tipo', value: 'kind', sortable: false, align: 'center', width: '130px' },
        { text: 'Código', value: 'code', sortable: false, align: 'center', width: '100px' },
        { text: 'Ações', value: 'actions', sortable: false, align: 'center', width: '180px' },
      ],
    }
  },
  computed: {
    is_root() {
      return this.depth === 0
    },
    kinds_to_expand_by_default() {
      return DEFAULT_EXPAND_KINDS[this.depth] || []
    },
    /** Raiz usa plano local; níveis filhos recebem prop do pai. */
    search_expand_plan_for_children() {
      return this.is_root ? this.active_search_expand_plan : this.searchExpandPlan
    },
    trimmed_search_text() {
      return (this.search || '').trim()
    },
    /** Texto da busca em minúsculas para comparar com nome e enviar ao backend (`q`). */
    search_text_lowercase() {
      return this.trimmed_search_text.toLowerCase()
    },
    /** Busca global ativa com termo não vazio (expandir árvore a partir do index). */
    has_global_search_term() {
      return this.globalSearch && this.trimmed_search_text.length > 0
    },
  },
  methods: {
    canAddChild(item) {
      return item && item.kind !== 'neighborhood'
    },
    kindLabel(kind) {
      return KIND_LABELS[kind] || kind
    },
    kindChipColor(kind) {
      let color_by_kind = {
        country: 'deep-purple',
        state: 'indigo',
        rgint: 'cyan darken-2',
        rgi: 'blue darken-2',
        city: 'teal',
        regional: 'amber darken-2',
        neighborhood: 'brown',
      }
      return color_by_kind[kind] || 'grey'
    },
    toggleNestedExpand(item) {
      let region_id = Number(item.id)
      let is_opening_branch = !this.expanded_nested_ids.includes(region_id)
      if (is_opening_branch) {
        this.loading_children_by_id = { ...this.loading_children_by_id, [region_id]: true }
      } else {
        this.loading_children_by_id = { ...this.loading_children_by_id, [region_id]: false }
      }
      let existing_index = this.expanded_nested_ids.indexOf(region_id)
      let next_expanded_nested_ids = [...this.expanded_nested_ids]
      if (existing_index === -1) next_expanded_nested_ids.push(region_id)
      else next_expanded_nested_ids.splice(existing_index, 1)
      this.expanded_nested_ids = next_expanded_nested_ids
    },
    toggleRootExpand(item, isExpanded, expand) {
      if (!isExpanded) {
        this.loading_children_by_id = { ...this.loading_children_by_id, [item.id]: true }
      } else {
        this.loading_children_by_id = { ...this.loading_children_by_id, [item.id]: false }
      }
      expand(!isExpanded)
    },
    setChildLoading(payload = {}) {
      let parent_region_id = payload.parentId
      let is_loading_children = payload.loading
      if (!parent_region_id) return
      this.loading_children_by_id = {
        ...this.loading_children_by_id,
        [parent_region_id]: Boolean(is_loading_children),
      }
    },
    applySearchExpandPlanAtLevel() {
      let search_expand_plan = this.search_expand_plan_for_children
      if (!this.has_global_search_term || !search_expand_plan) return

      if (this.is_root) {
        let expanded_root_id_set = new Set((search_expand_plan.expand_root_ids || []).map((id) => Number(id)))
        this.expanded = this.items.filter((row) => expanded_root_id_set.has(Number(row.id)))
        return
      }

      let parent_region_id = Number(this.parentId)
      let parent_region_key = String(parent_region_id)
      let child_ids_to_expand_from_plan = search_expand_plan.expand_children_under_parent[parent_region_key] || []
      let child_ids_to_expand_set = new Set(child_ids_to_expand_from_plan.map((id) => Number(id)))
      // Inclui qualquer filho listado no plano (mesmo folha): `has_children` quebrava o encadeamento
      // quando o match era o único filho sem descendentes na árvore exibida.
      let nested_row_ids_to_expand = this.items
        .filter((row) => child_ids_to_expand_set.has(Number(row.id)))
        .map((row) => Number(row.id))
      this.expanded_nested_ids = [...new Set([...this.expanded_nested_ids.map(Number), ...nested_row_ids_to_expand])]
    },
    applyDefaultExpansion() {
      if (this.has_global_search_term) return

      let kinds = this.kinds_to_expand_by_default
      if (!kinds.length || !this.items.length) return
      let rows_matching_default_kinds = this.items.filter((row) => row.has_children && kinds.includes(row.kind))
      if (!rows_matching_default_kinds.length) return

      this.$nextTick(() => {
        if (this.is_root) {
          this.expanded = [...rows_matching_default_kinds]
        } else {
          let default_expanded_nested_ids = rows_matching_default_kinds.map((row) => Number(row.id))
          this.expanded_nested_ids = [...new Set([...this.expanded_nested_ids, ...default_expanded_nested_ids])]
        }
      })
    },
    fetchNodes() {
      this.loading = true
      this.$emit('loading-change', { parentId: this.parentId, loading: true })
      let search_term_lowercase = this.search_text_lowercase
      if (this.globalSearch && this.is_root) {
        this.active_search_expand_plan = null
        Api.Region.index()
          .then((res) => res.data)
          .then((raw_tree) => {
            let tree = unwrapRegionsIndexPayload(raw_tree)
            let flat = dedupeRegionRowsPreferDeepestPath(flattenRegionsTree(tree))
            let matches = flat.filter((region_row) => {
              if (!search_term_lowercase) return true
              return String(region_row.name || '').toLowerCase().includes(search_term_lowercase)
            })
            if (!matches.length) {
              this.items = []
              this.total = 0
              this.expanded = []
              return Promise.resolve(null)
            }
            this.active_search_expand_plan = buildAncestorExpandPlan(matches)
            return Api.Region.treeChildren({ all: true })
          })
          .then((maybe_res) => {
            if (!maybe_res) return
            let body = maybe_res.data
            this.items = Array.isArray(body.data) ? body.data : []
            this.total = body.meta && typeof body.meta.total === 'number' ? body.meta.total : 0
          })
          .catch(() => {
            this.items = []
            this.total = 0
            this.expanded = []
            this.active_search_expand_plan = null
          })
          .finally(() => {
            this.loading = false
            this.$nextTick(() => {
              this.applySearchExpandPlanAtLevel()
            })
            this.$emit('loading-change', { parentId: this.parentId, loading: false })
          })
      } else {
        if (this.is_root) this.active_search_expand_plan = null
        let params = { all: true }
        if (this.parentId != null) params.parent_id = this.parentId
        // Busca global: o filtro por nome já roda na raiz (index + flatten); aqui só
        // listamos os filhos diretos do parent para montar o caminho expandido.
        // Enviar o parâmetro `q` junto com `parent_id` restringe cada nível ao pg_search.
        if (search_term_lowercase && !this.globalSearch) params.q = search_term_lowercase

        Api.Region.treeChildren(params)
          .then((res) => res.data)
          .then((body) => {
            this.items = Array.isArray(body.data) ? body.data : []
            this.total = body.meta && typeof body.meta.total === 'number' ? body.meta.total : 0
          })
          .catch(() => {
            this.items = []
            this.total = 0
          })
          .finally(() => {
            this.loading = false
            this.$nextTick(() => {
              this.applySearchExpandPlanAtLevel()
              this.applyDefaultExpansion()
            })
            this.$emit('loading-change', { parentId: this.parentId, loading: false })
          })
      }
    },
  },
  mounted() {
    this.fetchNodes()
  },
  watch: {
    search() {
      this.fetchNodes()
    },
    globalSearch() {
      this.fetchNodes()
    },
    refreshCounter() {
      this.expanded = []
      this.expanded_nested_ids = []
      if (this.is_root) this.active_search_expand_plan = null
      this.fetchNodes()
    },
    /** Reaplica expansão quando `items` chega após o plano (evita corrida com filhos aninhados). */
    items() {
      if (!this.has_global_search_term) return
      this.$nextTick(() => {
        this.applySearchExpandPlanAtLevel()
      })
    },
  },
}
</script>

<style scoped>
.region-tree-root-sheet {
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.region-tree-table--root >>> thead tr th {
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.54) !important;
  background: #fafbfc !important;
}

.region-tree-table--root >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
}

.region-tree-expand-placeholder {
  display: inline-block;
  width: 36px;
  height: 36px;
  vertical-align: middle;
}

.region-tree-expand-placeholder--sm {
  width: 28px;
  height: 28px;
}

.region-tree-name {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.82);
}

.region-tree-kind-chip {
  font-size: 11px !important;
  height: 22px !important;
}

.region-tree-expanded-td {
  background: linear-gradient(180deg, #f5f7fa 0%, #fafbfc 100%);
  border-top: none !important;
}

.region-tree-expanded-td--nested {
  background: #eef2f7;
}

.region-tree-nested-panel {
  padding: 12px 12px 8px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.region-tree-nested-panel--deep {
  padding-left: 12px;
}

.region-branch {
  display: flex;
  align-items: stretch;
  position: relative;
  min-height: 48px;
}

.region-branch__rail {
  flex: 0 0 4px;
  margin-right: 6px;
  border-radius: 4px 0 0 4px;
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.35), rgba(25, 118, 210, 0.08));
}

.region-branch__inner {
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  border-radius: 0 10px 10px 0;
  border: 1px solid rgba(0, 0, 0, 0.07);
  background: #fff;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.region-branch-th {
  font-size: 0.65rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.48) !important;
  height: 36px !important;
  background: #f5f7fa !important;
}

.region-branch-th--narrow {
  width: 44px;
}

.region-branch-th--actions {
  width: 140px;
}

.region-branch-row:hover {
  background: rgba(25, 118, 210, 0.04) !important;
}

.region-branch-td {
  vertical-align: middle !important;
}

.region-branch-td--narrow {
  width: 44px;
}

.region-branch-subrow td {
  border-top: none !important;
}

</style>
